import React from 'react'
import type { WithStyles, Theme } from '@material-ui/core'
import { withStyles, createStyles } from '@material-ui/core'
import DateIcon from '@material-ui/icons/CalendarToday'
import ArchiveIcon from '@material-ui/icons/Archive'
import moment from 'moment'
import numeral from 'numeral'
import type { QuoteRow } from './QuoteTable'
import { Typography, useClientOptions } from '@paintscout/ui'
import { QuoteTagIcons } from '../QuoteTagIcons'
import isEmpty from 'lodash/isEmpty'
import { getObjectLabels } from '@paintscout/util/builder'
import classNames from 'classnames'

export const styles = (theme: Theme) =>
  createStyles({
    root: {
      margin: `${theme.spacing(0.5)}px ${theme.spacing()}px`
    },
    hideName: {
      marginLeft: theme.spacing(5)
    },
    quoteValue: {
      fontWeight: theme.typography.fontWeightMedium,
      paddingBottom: theme.spacing(0.5)
    },
    mobileIcons: {
      fontSize: theme.typography.fontSize * 0.9,
      verticalAlign: 'middle',
      marginRight: theme.spacing(1),
      marginBottom: theme.spacing(0.25)
    },
    mobileValueContainer: {
      display: 'flex',
      alignItems: 'center',
      ...theme.typography.body1,
      color: theme.paintscout.palette.brand.Neutral.P70,
      width: '100%'
    },
    mobileNumber: {
      marginLeft: theme.spacing(0.5)
    },
    mobileEstimatorContainer: {
      ...theme.typography.body1,
      color: theme.paintscout.palette.brand.Neutral.P70,
      width: '100%'
    },
    mobileQuoteTypeContainer: {
      ...theme.typography.body1,
      color: theme.paintscout.palette.brand.Neutral.P70,
      width: '100%'
    },
    archivedRow: {
      display: 'flex',
      alignItems: 'center',
      ...theme.typography.body1,
      color: theme.paintscout.palette.brand.Neutral.P70,
      width: '100%'
    }
  })

export interface MobileViewColumnProps extends WithStyles<typeof styles> {
  row: QuoteRow
  hideName?: boolean
}

function MobileViewColumn({ classes, row, hideName }: MobileViewColumnProps) {
  const estimatorQuoteTypeValue: any[] = []
  if (row.quoteType) {
    estimatorQuoteTypeValue.push(row.quoteType)
  }
  if (row.estimator) {
    estimatorQuoteTypeValue.push(row.estimator)
  }

  const { options } = useClientOptions()
  const { currency } = getObjectLabels({ options })

  const number = row.number ? `#${row.number}` : null

  const value = `${currency.symbol}${numeral(row.value).format('0,0.00')}`

  const topRow = [hideName ? null : row.client, value].filter((item) => item).join(' - ')

  const dateFormat = options.options.dateFormat.momentValue

  const archived = row.archived ? (
    <div className={classes.archivedRow}>
      <ArchiveIcon className={classes.mobileIcons} />
      {' Archived'}
    </div>
  ) : null

  const estimatorQuoteType = estimatorQuoteTypeValue.length ? (
    <div className={classes.mobileEstimatorContainer}>
      <span>{estimatorQuoteTypeValue.filter((item) => item).join(' - ')}</span>
    </div>
  ) : null

  return (
    <div className={classNames({ [classes.root]: true, [classes.hideName]: hideName })}>
      {topRow && (
        <Typography variant={'body1'} className={classes.quoteValue}>
          {topRow}
        </Typography>
      )}
      <div className={classes.mobileValueContainer}>
        <DateIcon className={classes.mobileIcons} /> {moment(row.quote_date).format(dateFormat)}
        <span className={classes.mobileNumber}>{number}</span>
      </div>
      {archived}
      {estimatorQuoteType}
      {!isEmpty(row.tags) && <QuoteTagIcons tags={row.tags} />}
    </div>
  )
}

export default withStyles(styles)(MobileViewColumn)
